/**
 * PRODUCT FORM
 * 
 * This file handles product form submission and cart integration
 * with a clean, single-responsibility approach.
 */

console.log('🚀 PRODUCT FORM SCRIPT LOADING...');

console.log('🔍 Checking if product-form is already defined:', customElements.get('product-form'));

if (!customElements.get('product-form')) {
  console.log('🏗️ Defining product-form custom element...');
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        console.log('🏗️ ProductForm constructor called');
        console.log('🔍 ProductForm element:', this);

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        if (this.form) {
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
          console.log('✅ Form submit handler attached');
        } else {
          console.error('❌ No form found in product-form element');
        }
        this.submitButton = this.querySelector('button[type="submit"][name="add"]') || this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Initialize cart reference
        this.initializeCart();
      }

      initializeCart() {
        // Try to find cart drawer first, then fallback to notification
        // Support both V2 and legacy cart systems
        this.cart = document.querySelector('cart-drawer-v2') || 
                   document.querySelector('cart-drawer') || 
                   document.querySelector('cart-notification');
        console.log('🔍 Cart element found:', this.cart ? this.cart.tagName : 'None');
        
        // If V2 system is available, use it for add to cart
        if (window.cartCoreV2) {
          console.log('🎯 V2 system detected - will use V2 for add to cart');
          this.useV2System = true;
        } else {
          console.log('🔄 V2 system not detected - will use legacy system');
          this.useV2System = false;
        }
        
        console.log('🔍 Product form initialized:', {
          useV2System: this.useV2System,
          cartElement: this.cart ? this.cart.tagName : 'None',
          v2Available: !!window.cartCoreV2
        });
        
        // Force V2 system if available (temporary fix)
        if (window.cartCoreV2) {
          this.useV2System = true;
          console.log('🎯 FORCED V2 system usage');
        }
        
        // If no cart found, wait a bit and try again (for timing issues)
        if (!this.cart) {
          console.log('⏳ Cart not found, retrying in 100ms...');
          setTimeout(() => {
            this.cart = document.querySelector('cart-drawer-v2') || document.querySelector('cart-drawer') || document.querySelector('cart-notification');
            console.log('🔍 Cart element found (retry):', this.cart ? this.cart.tagName : 'None');
            this.updateSubmitButtonAttributes();
          }, 100);
        } else {
          this.updateSubmitButtonAttributes();
        }
      }

      updateSubmitButtonAttributes() {
        if (this.cart && this.cart.tagName === 'CART-DRAWER') {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      async onSubmitHandler(evt) {
        console.log('🚀 onSubmitHandler called');
        console.log('🔍 Event details:', {
          type: evt.type,
          target: evt.target.tagName,
          currentTarget: evt.currentTarget.tagName,
          defaultPrevented: evt.defaultPrevented
        });
        evt.preventDefault();
        if (!this.submitButton || this.submitButton.disabled || this.variantIdInput.disabled ||
            !this.variantIdInput.value || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        console.log('🛒 Starting add to cart process...');
        console.log('🔍 Current cart element:', this.cart ? this.cart.tagName : 'None');
        
        // Track add to cart attempts (V2 compatible)
        if (window.cartSystem && typeof window.cartSystem.trackEvent === 'function') {
          window.cartSystem.trackEvent('add_to_cart_started', {
            productId: this.variantIdInput.value,
            quantity: new FormData(this.form).get('quantity') || 1
          });
        } else {
          console.log('📊 Tracking not available (V2 system)');
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (window.cartCoreV2) {
          this.useV2System = true;
        }
        this.cart = document.querySelector('cart-drawer-v2') ||
                    document.querySelector('cart-drawer') ||
                    document.querySelector('cart-notification') ||
                    this.cart;
        
        // Drawer will open automatically after cart state loads in cart-core-v2.js

        // Dispatch an optimistic placeholder event (feature-flagged in CSS/JS)
        let optimisticDetail = null;
        try {
          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity')) || 1;
          const titleEl = document.querySelector('.product__title h1');
          const activeImg = document.querySelector('.ruemood-carousel-slide--active img');
          const placeholderId = 'ph-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);

          // Resolve selected variant payload from global script tag
          let selectedVariant = null;
          try {
            const sel = document.querySelector('script[type="application/json"][data-selected-variant]');
            if (sel && sel.textContent) {
              selectedVariant = JSON.parse(sel.textContent);
            }
          } catch (err) {
            console.debug('Failed to parse selected variant JSON', err);
          }

          // Determine price (cents)
          let priceCents = Number(selectedVariant?.price);
          if (!Number.isFinite(priceCents)) {
            const priceEl = document.querySelector('.price-item--regular, .price__regular .price-item');
            if (priceEl) {
              const m = priceEl.textContent.replace(/[^0-9.]/g, '');
              const num = parseFloat(m);
              if (!isNaN(num)) priceCents = Math.round(num * 100);
            }
          }
          if (!Number.isFinite(priceCents)) {
            priceCents = 0;
          }

          // Resolve image (prefer variant image when available)
          let imageSrc = selectedVariant?.featured_image?.src || selectedVariant?.featured_image?.url || '';
          if (!imageSrc && activeImg && activeImg.src) {
            imageSrc = activeImg.src;
          }
          if (imageSrc) {
            try {
              if (/([?&])width=\d+/.test(imageSrc)) {
                imageSrc = imageSrc.replace(/([?&])width=\d+/, '$1width=220');
              } else {
                imageSrc += imageSrc.includes('?') ? '&width=220' : '?width=220';
              }
            } catch (_) {}
          }

          // Map core properties (size/color etc.)
          const properties = {};
          for (const [key, value] of formData.entries()) {
            if (key.startsWith('properties[')) {
              const propKey = key.replace('properties[', '').replace(']', '');
              properties[propKey] = value;
            }
          }

          const normalizeProps = (props) => Object.keys(props).sort().reduce((acc, key) => {
            acc[key] = props[key];
            return acc;
          }, {});

          const lineIdentity = JSON.stringify({
            id: variantId,
            properties: normalizeProps(properties)
          });

          optimisticDetail = {
            placeholderId,
            variantId,
            quantity,
            title: titleEl ? titleEl.textContent.trim() : 'Adding to cart…',
            variantTitle: selectedVariant?.title || '',
            image: imageSrc,
            priceCents,
            properties,
            lineIdentity
          };

          this.lastOptimisticDetail = optimisticDetail;

          document.dispatchEvent(new CustomEvent('cart:adding', { detail: optimisticDetail }));
        } catch (e) {
          console.debug('cart:adding emit failed (non-blocking):', e);
        }
        
        // Use V2 system if available, otherwise use legacy
        if (this.useV2System && window.cartCoreV2) {
          console.log('🎯 Using V2 system for add to cart...');
          console.log('🔍 V2 system check:', {
            useV2System: this.useV2System,
            cartCoreV2Available: !!window.cartCoreV2,
            willUseV2: true
          });

          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity'), 10) || 1;
          const properties = {};
          for (const [key, value] of formData.entries()) {
            if (key.startsWith('properties[')) {
              const propKey = key.replace('properties[', '').replace(']', '');
              properties[propKey] = value;
            }
          }

          const normalizeProps = (props) => Object.keys(props).sort().reduce((acc, key) => {
            acc[key] = props[key];
            return acc;
          }, {});

          const fallbackIdentity = JSON.stringify({
            id: variantId,
            properties: normalizeProps(properties)
          });

          const optimisticMeta = this.lastOptimisticDetail || {};
          const lineIdentity = optimisticMeta.lineIdentity || fallbackIdentity;
          const placeholderId = optimisticMeta.placeholderId;

          window.cartCoreV2
            .addItem(variantId, quantity, properties, { lineIdentity, placeholderId })
            .then((success) => {
              if (!success) {
                console.error('❌ V2 add to cart failed');
                this.handleErrorMessage('Failed to add item to cart');
                return;
              }

              console.log('✅ V2 add to cart successful');
              // Open drawer after cart loads; waits for drawer readiness to avoid races
              if (window.cartCoreV2 && typeof window.cartCoreV2.openDrawerWhenReady === 'function') {
                window.cartCoreV2.openDrawerWhenReady();
              }
            })
            .catch((error) => {
              console.error('❌ V2 add to cart error:', error);
              this.handleErrorMessage('Something went wrong. Please try again.');
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('aria-disabled');
              const spinner = this.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');
            });

          return; // Exit early, don't use legacy fetch
        }

        // Legacy system
        console.log('🔄 Using legacy system for add to cart...');
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.section)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        
        config.body = formData;
        
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Handle error response
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              // Try one more time to find cart drawer; do NOT redirect
              this.cart = document.querySelector('cart-drawer-v2') || 
                         document.querySelector('cart-drawer') || 
                         document.querySelector('cart-notification');
              if (!this.cart) {
                console.log('⚠️ No cart drawer found after retry; staying on page and refreshing cart state.');
                // Ensure V2 opens if available, otherwise just refresh count
                if (window.cartCoreV2) {
                  try { window.cartCoreV2.openDrawer(); } catch (_) {}
                  window.cartCoreV2.loadCartState().catch(() => {});
                }
                return;
              }
              console.log('✅ Cart drawer found on retry:', this.cart.tagName);
            }

            if (!this.error) {
              // PHASE 2: Clean, single-path cart update
              console.log('🛒 Product added successfully, response:', response);
              console.log('🔍 Response item_count:', response.item_count);
              console.log('🔍 Full response keys:', Object.keys(response));
              
              // Step 1: Open cart drawer immediately (V2 first)
              if (window.cartCoreV2) {
                console.log('🚪 Opening cart drawer via V2 core...');
                window.cartCoreV2.openDrawer();
              } else if (this.cart && typeof this.cart.open === 'function') {
                console.log('🚪 Opening cart drawer via legacy...');
                this.cart.open();
              } else {
                console.log('⚠️ No cart drawer method found, trying to find drawer element...');
                const drawer = document.querySelector('cart-drawer-v2');
                if (drawer && drawer.open) {
                  drawer.open();
                }
              }
              
              // Step 2: Update cart contents with fresh data
              if (this.cart && typeof this.cart.renderContents === 'function') {
                console.log('🎨 Fetching fresh cart data...');
                
                // Use response data directly for now to avoid async issues
                console.log('🎨 Rendering cart contents with response data...');
                this.cart.renderContents(response);
              }
              
              // Step 3: Update cart count bubble
              if (response.item_count !== undefined && window.updateCartCount) {
                console.log('📊 Updating cart count bubble to:', response.item_count);
                setTimeout(() => {
                  window.updateCartCount(response.item_count);
                  console.log('✅ Cart count bubble updated');
                }, 50);
              } else {
                // Fallback: fetch current cart count if not in response
                console.log('⚠️ item_count not in response, fetching current cart...');
                
                // Always try V2 system first since it's default
                if (window.cartCoreV2) {
                  console.log('🔄 Using V2 system to refresh cart state...');
                  window.cartCoreV2.loadCartState().then(() => {
                    console.log('✅ V2 cart state refreshed');
                  }).catch(error => {
                    console.error('❌ V2 cart state refresh failed:', error);
                  });
                } else if (window.updateCartCount) {
                  // Legacy fallback
                  fetch('/cart.js')
                    .then(response => response.json())
                    .then(cart => {
                      console.log('📊 Fetched cart count:', cart.item_count);
                      setTimeout(() => {
                        window.updateCartCount(cart.item_count);
                        console.log('✅ Cart count bubble updated via fallback');
                      }, 50);
                    })
                    .catch(error => {
                      console.error('❌ Failed to fetch cart count:', error);
                    });
                }
              }
              
              // Step 4: Publish event for other subscribers
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response
              });
              
              // Track successful add to cart (V2 compatible)
              if (window.cartSystem && typeof window.cartSystem.trackEvent === 'function') {
                window.cartSystem.trackEvent('add_to_cart_completed', {
                  productId: formData.get('id'),
                  quantity: formData.get('quantity') || 1,
                  success: true,
                  itemCount: response.item_count
                });
              } else {
                console.log('📊 Success tracking not available (V2 system)');
              }
              
              console.log('✅ Cart update completed successfully');
            } else {
              console.log('❌ Error occurred during add to cart');
              
              // Track errors (V2 compatible)
              if (window.cartSystem && typeof window.cartSystem.trackEvent === 'function') {
                window.cartSystem.trackEvent('add_to_cart_error', {
                  productId: formData.get('id'),
                  error: 'form_error'
                });
              } else {
                console.log('📊 Error tracking not available (V2 system)');
              }
            }
            
            this.error = false;
            
            // Handle quick add modal
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  if (this.cart && this.cart.renderContents) {
                    this.cart.renderContents(response);
                  }
                },
                { once: true }
              );
              quickAddModal.hide(true);
            }
          })
          .catch((e) => {
            console.error('❌ Add to cart failed:', e);
            this.handleErrorMessage('Something went wrong. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        
        this.errorMessage = this.errorMessage || 
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // App blocks may contain another submit control or move it after load.
        // Resolve the actual cart button again instead of retaining that control.
        this.submitButton = this.querySelector('button[type="submit"][name="add"]') || this.querySelector('[type="submit"]');
        if (!this.submitButton) return;
        this.submitButtonText = this.submitButton.querySelector('span');
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
  console.log('✅ product-form custom element defined successfully');
} else {
  console.log('ℹ️ product-form custom element already exists');
}

console.log('✅ Product Form loaded successfully');

// Check if product-form elements exist on the page
setTimeout(() => {
  const productForms = document.querySelectorAll('product-form');
  console.log('🔍 Product form elements found:', productForms.length);
  
  productForms.forEach((form, index) => {
    console.log(`🔍 Product form ${index + 1}:`, {
      hasForm: !!form.querySelector('form'),
      hasSubmitButton: !!form.querySelector('[type="submit"]'),
      hasVariantInput: !!form.querySelector('[name="id"]'),
      isConnected: form.isConnected
    });
  });
}, 2000);

// Debug cart drawer availability immediately
console.log('🔍 Debug: Checking cart drawer availability...');
console.log('🔍 cart-drawer elements:', document.querySelectorAll('cart-drawer').length);
console.log('🔍 cart-notification elements:', document.querySelectorAll('cart-notification').length);
console.log('🔍 All cart-related elements:', document.querySelectorAll('[class*="cart"], [id*="cart"]').length);

// Wait for DOM to be fully loaded and check again
document.addEventListener('DOMContentLoaded', () => {
  console.log('🔍 DOM loaded - cart-drawer elements:', document.querySelectorAll('cart-drawer').length);
  console.log('🔍 DOM loaded - cart-notification elements:', document.querySelectorAll('cart-notification').length);
});
